<template>
  <div class="fence-register-page">
    <!-- Header de Navegação e Título -->
    <div class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-1">
      <div>
        <div class="d-flex align-items-center mb-25">
          <b-button
            variant="flat-primary"
            size="sm"
            class="btn-icon mr-50 p-25"
            :to="{ name: 'fence-management' }"
            v-b-tooltip.hover
            :title="$t('Voltar para Gestão de Cercas')"
          >
            <feather-icon icon="ArrowLeftIcon" size="18" />
          </b-button>
          <h3 class="font-weight-bolder text-primary mb-0">
            {{ isEditing ? $t('Editar Cerca Virtual') : $t('Nova Cerca Virtual') }}
          </h3>
        </div>
        <p class="text-muted font-small-3 mb-0 ml-2">
          {{ isEditing
            ? $t('Atualize os parâmetros de monitoramento e delimitação geográfica.')
            : $t('Preencha os dados e desenhe a área da cerca virtual diretamente no mapa.')
          }}
        </p>
      </div>

      <div class="d-flex align-items-center mt-1 mt-sm-0" style="gap: 8px;">
        <b-button
          variant="outline-secondary"
          size="sm"
          :to="{ name: 'fence-management' }"
          class="d-inline-flex align-items-center"
        >
          <feather-icon icon="XIcon" size="14" class="mr-50" />
          <span>{{ $t('Cancelar') }}</span>
        </b-button>

        <b-button
          variant="primary"
          size="sm"
          :disabled="isSaving"
          @click="submitForm"
          class="d-inline-flex align-items-center"
        >
          <b-spinner v-if="isSaving" small class="mr-50" />
          <feather-icon v-else icon="SaveIcon" size="14" class="mr-50" />
          <span>{{ isEditing ? $t('Salvar Alterações') : $t('Criar Cerca') }}</span>
        </b-button>
      </div>
    </div>

    <!-- Layout em Duas Colunas: Formulário à Esquerda e Mapa à Direita -->
    <b-row>
      <!-- ===================== COLUNA 1: DADOS DA CERCA ===================== -->
      <b-col cols="12" lg="5" xl="4">
        <!-- Card 1: Identificação -->
        <b-card no-body class="shadow-sm mb-1">
          <b-card-header class="py-75 px-1 border-bottom">
            <div class="d-flex align-items-center">
              <feather-icon icon="InfoIcon" size="16" class="text-primary mr-50" />
              <h6 class="mb-0 font-weight-bolder">{{ $t('Identificação') }}</h6>
            </div>
          </b-card-header>

          <b-card-body class="p-1">
            <!-- Nome da Cerca -->
            <b-form-group :label="$t('Nome da Cerca') + ' *'" label-for="fence-name" class="mb-1">
              <b-form-input
                id="fence-name"
                v-model="fenceData.name"
                :placeholder="$t('Ex: Galpão Central, Pátio Sul...')"
                :state="nameState"
              />
              <small v-if="nameState === false" class="text-danger font-small-1">
                {{ $t('Nome deve conter entre 3 e 50 caracteres.') }}
              </small>
            </b-form-group>

            <!-- Tipo de Cerca -->
            <b-form-group :label="$t('Tipo de Cerca')" label-for="fence-type" class="mb-1">
              <v-select
                id="fence-type"
                v-model="fenceData.virtualFenceTypeId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="typeOptions"
                :reduce="val => val.value"
                label="label"
                :clearable="true"
                :placeholder="$t('Selecione o tipo...')"
                input-id="fence-type"
              />
            </b-form-group>

            <!-- Limite de Velocidade -->
            <b-form-group :label="$t('Velocidade Máxima (km/h)')" label-for="fence-speed" class="mb-1">
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon icon="GaugeIcon" size="14" />
                </b-input-group-prepend>
                <b-form-input
                  id="fence-speed"
                  v-model="fenceData.maxSpeed"
                  type="number"
                  min="0"
                  max="200"
                  :placeholder="$t('Ex: 40 (opcional)')"
                />
              </b-input-group>
            </b-form-group>

            <!-- Cliente -->
            <b-form-group :label="$t('Cliente')" label-for="fence-customer" class="mb-0">
              <vue-autosuggest
                v-if="!isEditing"
                v-model="searchCustomerQuery"
                :suggestions="filteredOptionsCustomer"
                :input-props="inputPropsCustomer"
                @selected="selectHandlerCustomer"
                @input="onInputChangeCustomer"
              >
                <template slot-scope="{ suggestion }">
                  <div class="p-50 border-bottom">
                    <span class="font-weight-bold d-block">{{ suggestion.item.name }}</span>
                    <small v-if="suggestion.item.ownerName" class="text-muted">
                      {{ $t('Pertence a: ') }} {{ suggestion.item.ownerName }}
                    </small>
                  </div>
                </template>
              </vue-autosuggest>

              <div v-else class="p-50 bg-light rounded font-small-2 text-muted d-flex align-items-center">
                <feather-icon icon="LockIcon" size="14" class="mr-50 text-secondary" />
                <span>{{ currentCustomerName || $t('Cliente do usuário logado (inalterável)') }}</span>
              </div>
            </b-form-group>
          </b-card-body>
        </b-card>

        <!-- Card 2: Alertas de Monitoramento -->
        <b-card no-body class="shadow-sm mb-1">
          <b-card-header class="py-75 px-1 border-bottom">
            <div class="d-flex align-items-center">
              <feather-icon icon="BellIcon" size="16" class="text-primary mr-50" />
              <h6 class="mb-0 font-weight-bolder">{{ $t('Alertas de Monitoramento') }}</h6>
            </div>
          </b-card-header>

          <b-card-body class="p-1">
            <!-- Grid de Switches de Alertas -->
            <div class="alerts-switch-group">
              <!-- Entrada -->
              <div class="alert-switch-item d-flex align-items-center justify-content-between p-75 mb-50 rounded border">
                <span class="font-weight-bolder font-small-3 text-body">{{ $t('Alerta de Entrada') }}</span>
                <div class="custom-control custom-switch custom-control-primary">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    id="alertInSwitch"
                    v-model="fenceData.alertIn"
                  >
                  <label class="custom-control-label cursor-pointer" for="alertInSwitch"></label>
                </div>
              </div>

              <!-- Saída -->
              <div class="alert-switch-item d-flex align-items-center justify-content-between p-75 mb-50 rounded border">
                <span class="font-weight-bolder font-small-3 text-body">{{ $t('Alerta de Saída') }}</span>
                <div class="custom-control custom-switch custom-control-primary">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    id="alertOutSwitch"
                    v-model="fenceData.alertOut"
                  >
                  <label class="custom-control-label cursor-pointer" for="alertOutSwitch"></label>
                </div>
              </div>

              <!-- Permanece Dentro -->
              <div class="alert-switch-item d-flex align-items-center justify-content-between p-75 mb-50 rounded border">
                <span class="font-weight-bolder font-small-3 text-body">{{ $t('Permanece Dentro') }}</span>
                <div class="custom-control custom-switch custom-control-primary">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    id="alertStayInSwitch"
                    v-model="fenceData.alertStayIn"
                  >
                  <label class="custom-control-label cursor-pointer" for="alertStayInSwitch"></label>
                </div>
              </div>

              <!-- Permanece Fora -->
              <div class="alert-switch-item d-flex align-items-center justify-content-between p-75 rounded border">
                <span class="font-weight-bolder font-small-3 text-body">{{ $t('Permanece Fora') }}</span>
                <div class="custom-control custom-switch custom-control-primary">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    id="alertStayOutSwitch"
                    v-model="fenceData.alertStayOut"
                  >
                  <label class="custom-control-label cursor-pointer" for="alertStayOutSwitch"></label>
                </div>
              </div>
            </div>
          </b-card-body>
        </b-card>

        <!-- Card 3: Resumo Geométrico -->
        <b-card no-body class="shadow-sm mb-1">
          <b-card-header class="py-75 px-1 border-bottom">
            <div class="d-flex align-items-center">
              <feather-icon icon="CompassIcon" size="16" class="text-primary mr-50" />
              <h6 class="mb-0 font-weight-bolder">{{ $t('Dados da Geometria') }}</h6>
            </div>
          </b-card-header>

          <b-card-body class="p-1">
            <div v-if="hasDrawnGeometry" class="geometry-summary">
              <div class="d-flex justify-content-between py-25 border-bottom font-small-2">
                <span class="text-muted">{{ $t('Formato:') }}</span>
                <b-badge :variant="fenceData.virtualFenceGeometryId === 1 ? 'light-success' : 'light-primary'">
                  {{ fenceData.virtualFenceGeometryId === 1 ? $t('Círculo') : $t('Polígono') }}
                </b-badge>
              </div>

              <div v-if="fenceData.virtualFenceGeometryId === 1" class="d-flex justify-content-between py-25 border-bottom font-small-2">
                <span class="text-muted">{{ $t('Raio:') }}</span>
                <span class="font-weight-bold">{{ Math.round(fenceData.radius) }} m</span>
              </div>

              <div v-if="fenceData.virtualFenceGeometryId === 2" class="d-flex justify-content-between py-25 border-bottom font-small-2">
                <span class="text-muted">{{ $t('Vértices:') }}</span>
                <span class="font-weight-bold">{{ fenceData.points.length }} {{ $t('pontos') }}</span>
              </div>

              <div v-if="fenceData.lat && fenceData.lng" class="d-flex justify-content-between py-25 font-small-2">
                <span class="text-muted">{{ $t('Centro:') }}</span>
                <span class="font-weight-bold">{{ fenceData.lat.toFixed(5) }}, {{ fenceData.lng.toFixed(5) }}</span>
              </div>
            </div>

            <div v-else class="text-center py-1 text-muted">
              <feather-icon icon="MapPinIcon" size="20" class="mb-25 text-warning" />
              <p class="font-small-2 mb-0">
                {{ $t('Nenhuma área desenhada ainda. Use os botões de desenho no canto superior do mapa.') }}
              </p>
            </div>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- ===================== COLUNA 2: MAPA E DESENHO ===================== -->
      <b-col cols="12" lg="7" xl="8">
        <b-card no-body class="shadow-sm overflow-hidden mb-1">
          <!-- Barra Superior do Mapa: Busca e Instruções -->
          <div class="map-card-header p-1 bg-white border-bottom d-flex flex-column flex-md-row justify-content-between align-items-md-center" style="gap: 8px;">
            <div class="d-flex align-items-center">
              <feather-icon icon="Edit3Icon" size="18" class="text-primary mr-50" />
              <div>
                <h6 class="mb-0 font-weight-bolder">{{ $t('Desenho da Área no Mapa') }}</h6>
                <small class="text-muted">{{ drawingInstruction }}</small>
              </div>
            </div>

            <!-- Busca de Endereço no Mapa -->
            <div class="map-search-wrapper">
              <b-input-group size="sm">
                <b-form-input
                  v-model="locationQuery"
                  :placeholder="$t('Localizar endereço ou lat,lng...')"
                  @keyup.enter="searchLocation"
                />
                <b-input-group-append>
                  <b-button variant="primary" @click="searchLocation" :disabled="isSearchingLocation">
                    <feather-icon v-if="!isSearchingLocation" icon="SearchIcon" size="14" />
                    <b-spinner v-else small />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>
          </div>

          <!-- Container do Mapa -->
          <div class="map-wrapper position-relative" style="height: 620px;">
            <l-map ref="map" class="h-100 w-100" :zoom="zoom" :center="center" :max-zoom="18" @ready="onMapReady">
              <l-control-layers position="topright" />

              <!-- Base: OpenStreetMap -->
              <l-tile-layer
                :name="$t('Mapa Padrão')"
                :url="'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'"
                layer-type="base"
                checked
              />

              <!-- Base: ArcGIS Satélite -->
              <l-tile-layer
                :name="$t('Satélite')"
                :url="arcgisSatelliteUrl"
                layer-type="base"
              />

              <!-- Overlays -->
              <l-tile-layer
                :name="$t('Cidades / Locais')"
                :url="arcgisLabelsUrl"
                layer-type="overlay"
              />
              <l-tile-layer
                :name="$t('Ruas e Rodovias')"
                :url="arcgisRoadsUrl"
                layer-type="overlay"
              />
            </l-map>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  nextTick,
} from '@vue/composition-api'
import router from '@/router'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'

import L from 'leaflet'
import LDraw from 'leaflet-draw'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'

// Make Leaflet available globally so leaflet-draw can patch it
if (typeof window !== 'undefined') {
  window.L = L
}
import {
  LMap,
  LTileLayer,
  LControlLayers,
} from 'vue2-leaflet'

import {
  BCard,
  BCardBody,
  BCardHeader,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupAppend,
  BBadge,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { VueAutosuggest } from 'vue-autosuggest'

import fenceStoreModule from './fenceStoreModule'
import customerStoreModule from '@/views/main/customer/customerStoreModule'

export default {
  name: 'RegisterFence',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    LMap,
    LTileLayer,
    LControlLayers,
    BCard,
    BCardBody,
    BCardHeader,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BBadge,
    BSpinner,
    vSelect,
    VueAutosuggest,
  },
  setup(props, { root }) {
    const toast = useToast()
    const map = ref(null)

    const FENCE_APP_STORE_MODULE_NAME = 'app-fence'
    const CUSTOMER_APP_STORE_MODULE_NAME = 'app-customer'

    if (!store.hasModule(FENCE_APP_STORE_MODULE_NAME)) {
      store.registerModule(FENCE_APP_STORE_MODULE_NAME, fenceStoreModule)
    }
    if (!store.hasModule(CUSTOMER_APP_STORE_MODULE_NAME)) {
      store.registerModule(CUSTOMER_APP_STORE_MODULE_NAME, customerStoreModule)
    }

    onUnmounted(() => {
      if (store.hasModule(FENCE_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(FENCE_APP_STORE_MODULE_NAME)
      }
    })

    const isEditing = computed(() => Boolean(router.currentRoute.params.id))
    const currentFenceId = computed(() => router.currentRoute.params.id)

    // Estado do formulário
    const fenceData = ref({
      id: '',
      name: '',
      customerId: null,
      lat: 0,
      lng: 0,
      radius: 0,
      alertIn: false,
      alertOut: false,
      alertStayIn: false,
      alertStayOut: false,
      maxSpeed: null,
      virtualFenceGeometryId: 0,
      virtualFenceTypeId: null,
      points: [],
    })

    const typeOptions = ref([])
    const currentCustomerName = ref('')
    const searchCustomerQuery = ref('')
    const filteredOptionsCustomer = ref([])
    const isSaving = ref(false)

    // Autosuggest de cliente
    const inputPropsCustomer = computed(() => ({
      id: 'autosuggest-customer-register',
      class: 'form-control',
      placeholder: root.$t('Pesquisar cliente associado...'),
    }))

    const nameState = computed(() => {
      if (!fenceData.value.name) return null
      return fenceData.value.name.length >= 3 && fenceData.value.name.length <= 50
    })

    const hasDrawnGeometry = computed(() => {
      if (fenceData.value.virtualFenceGeometryId === 1 && fenceData.value.radius > 0) return true
      if (fenceData.value.virtualFenceGeometryId === 2 && fenceData.value.points && fenceData.value.points.length >= 3) return true
      return false
    })

    const drawingInstruction = computed(() => {
      if (hasDrawnGeometry.value) {
        return root.$t('Geometria desenhada. Use o botão de editar para ajustar os limites ou lixeira para refazer.')
      }
      return root.$t('Utilize a barra de ferramentas de desenho no canto superior esquerdo do mapa para desenhar um Círculo ou Polígono.')
    })

    // Mapa e camadas
    const arcgisSatelliteUrl = 'https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
    const arcgisLabelsUrl = 'https://services.arcgisonline.com/arcgis/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'
    const arcgisRoadsUrl = 'https://services.arcgisonline.com/arcgis/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}'

    // Localização padrão ou passada por query
    const criterion = router.currentRoute.query && router.currentRoute.query.criterion ? router.currentRoute.query.criterion : null
    const center = ref(criterion && criterion.lat ? [criterion.lat, criterion.lng] : [-19.801187, -43.948885])
    const zoom = ref(criterion && criterion.zoom ? Number(criterion.zoom) : 7)

    const locationQuery = ref('')
    const isSearchingLocation = ref(false)

    // Leaflet Draw referências
    let drawnItems = null
    let drawControl = null
    let drawEditOnly = null

    const translateLDraw = () => {
      const wL = window.L
      if (!wL || !wL.drawLocal) return
      
      wL.drawLocal.draw.toolbar.actions.title = root.$t('Cancelar desenho')
      wL.drawLocal.draw.toolbar.actions.text = root.$t('Cancel')
      wL.drawLocal.draw.toolbar.finish.title = root.$t('Finalizar desenho')
      wL.drawLocal.draw.toolbar.finish.text = root.$t('Finalizar')
      wL.drawLocal.draw.toolbar.undo.title = root.$t('Desfazer último ponto')
      wL.drawLocal.draw.toolbar.undo.text = root.$t('Desfazer')
      
      wL.drawLocal.draw.toolbar.buttons.polygon = root.$t('Desenhar Polígono')
      wL.drawLocal.draw.toolbar.buttons.circle = root.$t('Desenhar Círculo')

      wL.drawLocal.draw.handlers.circle.tooltip.start = root.$t('Clique e arraste para desenhar o círculo')
      wL.drawLocal.draw.handlers.circle.radius = root.$t('Raio')

      wL.drawLocal.draw.handlers.polygon.tooltip.start = root.$t('Clique para começar a desenhar o polígono')
      wL.drawLocal.draw.handlers.polygon.tooltip.cont = root.$t('Clique para continuar inserindo vértices')
      wL.drawLocal.draw.handlers.polygon.tooltip.end = root.$t('Clique no primeiro ponto para fechar a área')
      
      wL.drawLocal.draw.handlers.simpleshape.tooltip.end = root.$t('Solte o mouse para finalizar o desenho')

      wL.drawLocal.edit.toolbar.actions.save.title = root.$t('Salvar alterações')
      wL.drawLocal.edit.toolbar.actions.save.text = root.$t('Salvar')
      wL.drawLocal.edit.toolbar.actions.cancel.title = root.$t('Cancelar edição')
      wL.drawLocal.edit.toolbar.actions.cancel.text = root.$t('Cancelar')
      wL.drawLocal.edit.toolbar.actions.clearAll.title = root.$t('Limpar tudo')
      wL.drawLocal.edit.toolbar.actions.clearAll.text = root.$t('Limpar')

      wL.drawLocal.edit.toolbar.buttons.edit = root.$t('Editar vértices')
      wL.drawLocal.edit.toolbar.buttons.editDisabled = root.$t('Nenhuma geometria para editar')
      wL.drawLocal.edit.toolbar.buttons.remove = root.$t('Remover geometria')
      wL.drawLocal.edit.toolbar.buttons.removeDisabled = root.$t('Nenhuma geometria para remover')

      wL.drawLocal.edit.handlers.edit.tooltip.text = root.$t('Arraste os nós para ajustar os limites')
      wL.drawLocal.edit.handlers.edit.tooltip.subtext = root.$t('Clique em cancelar para desfazer')
      wL.drawLocal.edit.handlers.remove.tooltip.text = root.$t('Clique na cerca para removê-la')
    }

    let isDrawInitialized = false

    const initDrawControls = mapInstance => {
      try {
        const mapObj = mapInstance || (map.value && map.value.mapObject)
        if (!mapObj || isDrawInitialized) return
        isDrawInitialized = true

        translateLDraw()

        const wL = window.L

        if (!wL || !wL.Control || !wL.Control.Draw) {
          throw new Error("L.Control.Draw não está definido. O leaflet-draw não foi carregado corretamente.")
        }

        drawnItems = new wL.FeatureGroup()
        mapObj.addLayer(drawnItems)

        drawControl = new wL.Control.Draw({
          position: 'topleft',
          draw: {
            polyline: false,
            rectangle: false,
            marker: false,
            circlemarker: false,
            circle: {
              shapeOptions: {
                color: '#28c76f',
                fillColor: '#28c76f',
                fillOpacity: 0.2,
                weight: 2,
              },
            },
            polygon: {
              allowIntersection: false,
              shapeOptions: {
                color: '#7367f0',
                fillColor: '#7367f0',
                fillOpacity: 0.2,
                weight: 2,
              },
            },
          },
          edit: false,
        })

        drawEditOnly = new wL.Control.Draw({
          position: 'topleft',
          draw: false,
          edit: {
            featureGroup: drawnItems,
            remove: true,
          },
        })

        // Se estiver editando ou já tiver desenho, inicia modo de edição
        if (isEditing.value) {
          mapObj.addControl(drawEditOnly)
        } else {
          mapObj.addControl(drawControl)
        }

        // Evento: Criação de forma
        mapObj.on(wL.Draw.Event.CREATED, e => {
          const layer = e.layer
          drawnItems.clearLayers()
          drawnItems.addLayer(layer)

        if (layer instanceof L.Circle) {
          fenceData.value.virtualFenceGeometryId = 1
          fenceData.value.radius = layer.getRadius()
          const latlng = layer.getLatLng()
          fenceData.value.lat = latlng.lat
          fenceData.value.lng = latlng.lng
          fenceData.value.points = []
        } else if (layer instanceof L.Polygon) {
          fenceData.value.virtualFenceGeometryId = 2
          const latlngs = layer.getLatLngs()
          const pointsList = latlngs[0] || []
          fenceData.value.points = pointsList.map((pt, index) => ({
            order: index + 1,
            lat: pt.lat,
            lng: pt.lng,
          }))

          if (pointsList.length > 0) {
            fenceData.value.lat = pointsList[0].lat
            fenceData.value.lng = pointsList[0].lng
          }
          fenceData.value.radius = 0
        }

        mapObj.removeControl(drawControl)
        mapObj.addControl(drawEditOnly)
      })

      // Evento: Edição de forma
      mapObj.on('draw:edited', e => {
        const layers = e.layers.getLayers()
        if (layers.length === 0) return
        const layer = layers[0]

        if (layer instanceof L.Circle) {
          fenceData.value.virtualFenceGeometryId = 1
          fenceData.value.radius = layer.getRadius()
          const latlng = layer.getLatLng()
          fenceData.value.lat = latlng.lat
          fenceData.value.lng = latlng.lng
        } else if (layer instanceof L.Polygon) {
          fenceData.value.virtualFenceGeometryId = 2
          const latlngs = layer.getLatLngs()
          const pointsList = latlngs[0] || []
          fenceData.value.points = pointsList.map((pt, index) => ({
            order: index + 1,
            lat: pt.lat,
            lng: pt.lng,
          }))

          if (pointsList.length > 0) {
            fenceData.value.lat = pointsList[0].lat
            fenceData.value.lng = pointsList[0].lng
          }
        }
      })

      // Evento: Exclusão de forma
      mapObj.on('draw:deleted', () => {
        if (drawnItems.getLayers().length === 0) {
          fenceData.value.virtualFenceGeometryId = 0
          fenceData.value.lat = 0
          fenceData.value.lng = 0
          fenceData.value.radius = 0
          fenceData.value.points = []

          mapObj.removeControl(drawEditOnly)
          mapObj.addControl(drawControl)
        }
      })
      } catch (error) {
        console.error("Erro ao inicializar leaflet-draw:", error)
        toast.error(`Erro ao carregar ferramentas de desenho: ${error.message}`)
      }
    }

    const loadFenceDetails = async (id, mapInstance) => {
      try {
        const response = await store.dispatch(`${FENCE_APP_STORE_MODULE_NAME}/fetchFence`, { id })
        const data = response.data
        fenceData.value = {
          ...data,
          alertIn: Boolean(data.alertIn),
          alertOut: Boolean(data.alertOut),
          alertStayIn: Boolean(data.alertStayIn),
          alertStayOut: Boolean(data.alertStayOut),
        }
        currentCustomerName.value = data.customerName || ''

        nextTick(() => {
          const mapObj = mapInstance || (map.value && map.value.mapObject)
          if (!mapObj || !drawnItems) return
          drawnItems.clearLayers()

          if (data.virtualFenceGeometryId === 1 && data.lat && data.lng) {
            const circleLayer = L.circle([data.lat, data.lng], {
              radius: data.radius,
              color: '#28c76f',
              fillColor: '#28c76f',
              fillOpacity: 0.2,
              weight: 2,
            })
            drawnItems.addLayer(circleLayer)
            mapObj.setView([data.lat, data.lng], 14)
          } else if (data.virtualFenceGeometryId === 2 && Array.isArray(data.points) && data.points.length > 0) {
            const sorted = [...data.points].sort((a, b) => (a.order > b.order ? 1 : -1))
            const latlngs = sorted.map(p => [p.lat, p.lng])
            const polyLayer = L.polygon(latlngs, {
              color: '#7367f0',
              fillColor: '#7367f0',
              fillOpacity: 0.2,
              weight: 2,
            })
            drawnItems.addLayer(polyLayer)
            mapObj.fitBounds(latlngs, { padding: [50, 50], maxZoom: 16 })
          }
        })
      } catch (error) {
        toast.error(root.$t('Erro ao carregar detalhes da cerca.'))
      }
    }

    const loadSearchTypes = async () => {
      try {
        const response = await store.dispatch(`${FENCE_APP_STORE_MODULE_NAME}/fetchSearchtype`)
        const list = response.data.data || response.data || []
        typeOptions.value = list.map(t => ({
          label: t.name || t.label,
          value: t.id,
        }))
      } catch (_) {
        typeOptions.value = []
      }
    }

    // Handlers do Autosuggest de Cliente
    const selectHandlerCustomer = option => {
      if (option && option.item) {
        fenceData.value.customerId = option.item.id
        searchCustomerQuery.value = option.item.name
        filteredOptionsCustomer.value = []
      }
    }

    const onInputChangeCustomer = text => {
      if (!text || text.length < 2) {
        fenceData.value.customerId = null
        filteredOptionsCustomer.value = []
        return
      }

      store
        .dispatch(`${CUSTOMER_APP_STORE_MODULE_NAME}/fetchCustomers`, {
          q: text,
          page: 1,
          perPage: 6,
        })
        .then(response => {
          filteredOptionsCustomer.value = [{ data: response.data.data || [] }]
        })
        .catch(() => {
          filteredOptionsCustomer.value = []
        })
    }

    // Busca de Endereço / Coordenadas
    const searchLocation = async () => {
      if (!locationQuery.value || locationQuery.value.trim() === '') {
        toast.warning(root.$t('Please enter an address or coordinates'))
        return
      }

      isSearchingLocation.value = true

      const coordRegex = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/
      if (coordRegex.test(locationQuery.value.trim())) {
        const [lat, lng] = locationQuery.value.split(',').map(Number)
        if (map.value && map.value.mapObject) {
          map.value.mapObject.flyTo([lat, lng], 15)
        }
        isSearchingLocation.value = false
        return
      }

      try {
        const response = await store.dispatch(`${FENCE_APP_STORE_MODULE_NAME}/fetchAddres`, {
          address: locationQuery.value.trim(),
        })

        if (!response.data || response.data.length === 0) {
          toast.error(root.$t('Address not found'))
          return
        }

        const res = response.data[0]
        const lat = parseFloat(res.lat)
        const lon = parseFloat(res.lon)

        if (map.value && map.value.mapObject) {
          map.value.mapObject.flyTo([lat, lon], 14, { duration: 1.2 })
        }
        toast.success(root.$t('Localização encontrada'))
      } catch (e) {
        toast.error(root.$t('Erro ao buscar endereço'))
      } finally {
        isSearchingLocation.value = false
      }
    }

    // Submissão do Formulário
    const submitForm = async () => {
      if (!fenceData.value.name || fenceData.value.name.trim().length < 3) {
        toast.warning(root.$t('Por favor, informe um nome com pelo menos 3 caracteres.'))
        return
      }

      if (!hasDrawnGeometry.value) {
        toast.warning(root.$t('Por favor, desenhe uma área no mapa para a cerca virtual.'))
        return
      }

      isSaving.value = true

      try {
        if (isEditing.value) {
          await store.dispatch(`${FENCE_APP_STORE_MODULE_NAME}/updateFence`, {
            fenceData: fenceData.value,
          })
          toast.success(root.$t('Cerca virtual atualizada com sucesso!'))
        } else {
          await store.dispatch(`${FENCE_APP_STORE_MODULE_NAME}/addFence`, {
            fenceData: fenceData.value,
          })
          toast.success(root.$t('Cerca virtual cadastrada com sucesso!'))
        }

        router.push({ name: 'fence-management' })
      } catch (error) {
        toast.error(root.$t('Erro ao salvar cerca virtual. Verifique os dados e tente novamente.'))
      } finally {
        isSaving.value = false
      }
    }

    const onMapReady = mapInstance => {
      const mapObj = mapInstance || (map.value && map.value.mapObject)
      if (!mapObj) return
      initDrawControls(mapObj)
      if (isEditing.value && currentFenceId.value) {
        loadFenceDetails(currentFenceId.value, mapObj)
      }
    }

    onMounted(() => {
      loadSearchTypes()

      nextTick(() => {
        if (map.value && map.value.mapObject) {
          onMapReady(map.value.mapObject)
        }
      })
    })

    return {
      map,
      onMapReady,
      isEditing,
      fenceData,
      typeOptions,
      currentCustomerName,
      searchCustomerQuery,
      filteredOptionsCustomer,
      inputPropsCustomer,
      nameState,
      hasDrawnGeometry,
      drawingInstruction,
      isSaving,
      center,
      zoom,
      locationQuery,
      isSearchingLocation,
      arcgisSatelliteUrl,
      arcgisLabelsUrl,
      arcgisRoadsUrl,
      selectHandlerCustomer,
      onInputChangeCustomer,
      searchLocation,
      submitForm,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';

</style>

<style lang="scss" scoped>
.fence-register-page {
  position: relative;
}

.map-card-header {
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.map-search-wrapper {
  width: 280px;
  max-width: 100%;
}

.alerts-switch-group {
  display: flex;
  flex-direction: column;
}

.alert-switch-item {
  background: #fbfbfd;
  transition: background 0.2s ease;

  &:hover {
    background: #f1f2f6;
  }
}
</style>
